import {
  Bell,
  FileText,
  Globe2,
  Link2,
  Mail,
  Monitor,
  Paintbrush,
  Plus,
  Search,
  ShieldCheck,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { BusinessCenterOSAdminShell } from "../../components/layout/BusinessCenterOSAdminShell";
import { formatDate } from "../../lib/format";
import type {
  BusinessCenterBrandingProfileRecord,
  BusinessCenterCompanyRecord,
  BusinessCenterOnboardingRecord,
} from "../../lib/types";
import {
  buildBusinessCenterBrandingDashboard,
  buildBusinessCenterBrandingPreview,
  createBusinessCenterBrandingProfile,
  loadBusinessCenterBrandingProfiles,
  refreshBusinessCenterBrandingProfiles,
  resolveBusinessCenterBrandingProfile,
  updateBusinessCenterBrandingProfile,
  upsertBusinessCenterBrandingProfile,
} from "./businessCenterBrandingData";
import { loadBusinessCenterCompanies, refreshBusinessCenterCompanies } from "./businessCenterCompanyUsersData";
import { loadBusinessCenterOnboardingRecords, refreshBusinessCenterOnboardingRecords } from "./businessCenterOnboardingData";

type BrandFormState = {
  companyId: string;
  companyName: string;
  companySlug: string;
  publicDisplayName: string;
  logoUrl: string;
  faviconUrl: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  contactEmail: string;
  supportEmail: string;
  phoneNumber: string;
  website: string;
  companyDescription: string;
  portalName: string;
  portalSubtitle: string;
  portalLogoUrl: string;
  portalPrimaryColor: string;
  portalSecondaryColor: string;
  portalAccentColor: string;
  portalWelcomeMessage: string;
  documentLogoUrl: string;
  footerText: string;
  signatureBlock: string;
  companyAddress: string;
  companyContactInformation: string;
  emailLogoUrl: string;
  emailHeaderColor: string;
  emailFooterColor: string;
  emailSignature: string;
  emailCompanyFooter: string;
  primaryDomain: string;
  portalDomain: string;
  customSubdomain: string;
  loginUrl: string;
  showPoweredByBusinessCenterOS: boolean;
  showWorkspaceName: boolean;
  showCompanyName: boolean;
};

const blankForm: BrandFormState = {
  companyId: "",
  companyName: "",
  companySlug: "",
  publicDisplayName: "",
  logoUrl: "",
  faviconUrl: "",
  primaryColor: "#1F2937",
  secondaryColor: "#334155",
  accentColor: "#06B6D4",
  contactEmail: "",
  supportEmail: "",
  phoneNumber: "",
  website: "",
  companyDescription: "",
  portalName: "",
  portalSubtitle: "",
  portalLogoUrl: "",
  portalPrimaryColor: "#1F2937",
  portalSecondaryColor: "#334155",
  portalAccentColor: "#06B6D4",
  portalWelcomeMessage: "",
  documentLogoUrl: "",
  footerText: "",
  signatureBlock: "",
  companyAddress: "",
  companyContactInformation: "",
  emailLogoUrl: "",
  emailHeaderColor: "#1F2937",
  emailFooterColor: "#334155",
  emailSignature: "",
  emailCompanyFooter: "",
  primaryDomain: "",
  portalDomain: "",
  customSubdomain: "",
  loginUrl: "",
  showPoweredByBusinessCenterOS: true,
  showWorkspaceName: true,
  showCompanyName: true,
};

export function WhiteLabelBrandingCenterPage() {
  const [companies, setCompanies] = useState<BusinessCenterCompanyRecord[]>([]);
  const [onboarding, setOnboarding] = useState<BusinessCenterOnboardingRecord[]>([]);
  const [profiles, setProfiles] = useState<BusinessCenterBrandingProfileRecord[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [form, setForm] = useState<BrandFormState>(blankForm);

  useBusinessCenterOSBranding(
    "White Label Branding Center",
    "Company-scoped branding configuration for portals, documents, emails, domains, and white-label presentation layers.",
  );

  useEffect(() => {
    return loadWorkspace();
  }, []);

  const visibleCompanies = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return companies.filter((company) => {
      if (!normalized) return company.companyType !== "parent";
      return (
        company.companyType !== "parent" &&
        [company.companyName, company.companySlug, company.brand, company.status]
          .join(" ")
          .toLowerCase()
          .includes(normalized)
      );
    });
  }, [companies, query]);

  const dashboard = useMemo(() => buildBusinessCenterBrandingDashboard(profiles), [profiles]);

  const selectedProfile = useMemo(() => {
    if (!selectedCompanyId) return null;
    return profiles.find((profile) => profile.companyId === selectedCompanyId || profile.id === selectedCompanyId) || null;
  }, [profiles, selectedCompanyId]);

  const previewProfile = useMemo(
    () =>
      buildProfileFromForm(
        form,
        selectedProfile?.events || [],
        selectedProfile?.sourceOnboardingId || null,
      ),
    [form, selectedProfile?.events, selectedProfile?.sourceOnboardingId],
  );
  const previews = useMemo(() => buildBusinessCenterBrandingPreview(previewProfile), [previewProfile]);

  useEffect(() => {
    if (selectedCompanyId && visibleCompanies.every((company) => company.id !== selectedCompanyId) && visibleCompanies[0]) {
      setSelectedCompanyId(visibleCompanies[0].id);
    }
    if (!selectedCompanyId && visibleCompanies[0]) {
      setSelectedCompanyId(visibleCompanies[0].id);
    }
  }, [selectedCompanyId, visibleCompanies]);

  useEffect(() => {
    if (!selectedCompanyId) return;
    const company = companies.find((item) => item.id === selectedCompanyId);
    if (!company) return;
    const profile =
      resolveBusinessCenterBrandingProfile(company.id, company.companySlug, company.companyName, company.brand) ||
      buildProfileFromCompany(company, onboarding);
    setForm(buildFormFromProfile(profile));
  }, [companies, onboarding, selectedCompanyId]);

  function loadWorkspace() {
    setLoading(true);
    setError("");
    let cancelled = false;
    Promise.all([
      refreshBusinessCenterCompanies().catch(() => loadBusinessCenterCompanies()),
      refreshBusinessCenterOnboardingRecords().catch(() => loadBusinessCenterOnboardingRecords()),
      refreshBusinessCenterBrandingProfiles().catch(() => loadBusinessCenterBrandingProfiles()),
    ])
      .then(([nextCompanies, nextOnboarding, nextProfiles]) => {
        if (cancelled) return;
        setCompanies(nextCompanies);
        setOnboarding(nextOnboarding);
        setProfiles(nextProfiles);
        const firstCompany = nextCompanies.find((company) => company.companyType !== "parent") || null;
        const nextSelected = nextProfiles[0]?.companyId || firstCompany?.id || "";
        setSelectedCompanyId((current) => (nextCompanies.some((company) => company.id === current) ? current : nextSelected));
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError instanceof Error ? loadError.message : "Unable to load the branding workspace.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }

  function updateForm<K extends keyof BrandFormState>(key: K, value: BrandFormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function populateCompany(companyId: string) {
    const company = companies.find((item) => item.id === companyId);
    if (!company) return;
    setSelectedCompanyId(companyId);
    const profile =
      resolveBusinessCenterBrandingProfile(company.id, company.companySlug, company.companyName, company.brand) ||
      buildProfileFromCompany(company, onboarding);
    setForm(buildFormFromProfile(profile));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    setError("");
    try {
      const nextProfile = buildProfileFromForm(
        form,
        selectedProfile?.events || [],
        selectedProfile?.sourceOnboardingId || null,
      );
      upsertBusinessCenterBrandingProfile(nextProfile);
      setMessage(`Saved branding profile for ${nextProfile.companyName}.`);
      loadWorkspace();
      setSelectedCompanyId(nextProfile.companyId);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Unable to save branding profile.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <BusinessCenterOSAdminShell
      title="White Label Branding Center"
      description="One shared branding source of truth for portals, documents, emails, domains, and company presentation across all tenants."
      statusItems={[
        { label: "Profiles", value: dashboard.totalProfiles },
        { label: "Domain ready", value: dashboard.activeDomains },
        { label: "Portal ready", value: dashboard.portalReady },
        { label: "Logo ready", value: dashboard.logoReady },
      ]}
    >
      <section className="py-2">
        <div className="mx-auto grid max-w-7xl gap-6 xl:grid-cols-[1.12fr_.88fr]">
          <div className="grid gap-6">
            <article className="business-center-os-card grid gap-5 p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-[0.68rem] font-black uppercase tracking-[0.22em] text-[#C4B5FD]">Company selector</p>
                  <h2 className="mt-2 text-2xl font-black text-white">Branding profiles by company</h2>
                  <p className="mt-1 max-w-3xl text-sm leading-6 text-[#C9D6DF]">
                    Every profile stays scoped to one company and can be reused by onboarding, portal, document, and email surfaces.
                  </p>
                </div>
                <div className="relative min-w-72 flex-1 max-w-xl">
                  <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-white/45" aria-hidden="true" />
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    className="business-center-os-input pl-11"
                    placeholder="Search companies by name, slug, brand..."
                  />
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                {visibleCompanies.map((company) => {
                  const profile = profiles.find((item) => item.companyId === company.id);
                  const onboardingRecord = onboarding.find((item) => item.companyId === company.id);
                  const isSelected = selectedCompanyId === company.id;
                  return (
                    <button
                      key={company.id}
                      type="button"
                      onClick={() => populateCompany(company.id)}
                      className={`grid gap-3 rounded-3xl border p-4 text-left transition ${
                        isSelected
                          ? "border-[rgba(168,85,247,.55)] bg-[rgba(168,85,247,.08)]"
                          : "border-[rgba(255,255,255,.08)] bg-[rgba(255,255,255,.02)] hover:border-[rgba(168,85,247,.35)] hover:bg-[rgba(255,255,255,.04)]"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="grid gap-1">
                          <h3 className="text-lg font-black text-white">{company.companyName}</h3>
                          <p className="text-sm font-semibold text-[#C9D6DF]">{company.companySlug}</p>
                        </div>
                        <span className="rounded-full border border-[rgba(168,85,247,.2)] px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.16em] text-[#C4B5FD]">
                          {company.brand}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs font-bold text-[#D9E6F2]">
                        <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1">Portal: {profile?.portal.name || "draft"}</span>
                        <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1">Onboarding: {onboardingRecord?.status || "n/a"}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </article>

            <article className="business-center-os-card grid gap-5 p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-[0.68rem] font-black uppercase tracking-[0.22em] text-[#C4B5FD]">Branding profile</p>
                  <h2 className="mt-2 text-2xl font-black text-white">Company and portal identity</h2>
                </div>
                <div className="rounded-2xl border border-[rgba(168,85,247,.18)] bg-[rgba(255,255,255,.03)] px-4 py-3 text-sm font-semibold text-[#E2E8F0]">
                  Live profile: {selectedProfile?.publicDisplayName || form.publicDisplayName || "Draft branding"}
                </div>
              </div>

              <form className="grid gap-5" onSubmit={handleSubmit}>
                <div className="grid gap-4 lg:grid-cols-2">
                  <Field label="Company name">
                    <input value={form.companyName} onChange={(event) => updateForm("companyName", event.target.value)} className="business-center-os-input" />
                  </Field>
                  <Field label="Public display name">
                    <input value={form.publicDisplayName} onChange={(event) => updateForm("publicDisplayName", event.target.value)} className="business-center-os-input" />
                  </Field>
                  <Field label="Company slug">
                    <input value={form.companySlug} onChange={(event) => updateForm("companySlug", event.target.value)} className="business-center-os-input" />
                  </Field>
                  <Field label="Website">
                    <input value={form.website} onChange={(event) => updateForm("website", event.target.value)} className="business-center-os-input" />
                  </Field>
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  <Field label="Logo upload">
                    <input value={form.logoUrl} onChange={(event) => updateForm("logoUrl", event.target.value)} className="business-center-os-input" placeholder="logo.svg or CDN URL" />
                  </Field>
                  <Field label="Favicon upload">
                    <input value={form.faviconUrl} onChange={(event) => updateForm("faviconUrl", event.target.value)} className="business-center-os-input" placeholder="favicon.ico or SVG URL" />
                  </Field>
                  <Field label="Primary color">
                    <input value={form.primaryColor} onChange={(event) => updateForm("primaryColor", event.target.value)} className="business-center-os-input" />
                  </Field>
                  <Field label="Secondary color">
                    <input value={form.secondaryColor} onChange={(event) => updateForm("secondaryColor", event.target.value)} className="business-center-os-input" />
                  </Field>
                  <Field label="Accent color">
                    <input value={form.accentColor} onChange={(event) => updateForm("accentColor", event.target.value)} className="business-center-os-input" />
                  </Field>
                  <Field label="Company description">
                    <textarea value={form.companyDescription} onChange={(event) => updateForm("companyDescription", event.target.value)} className="business-center-os-input min-h-28" />
                  </Field>
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  <Field label="Contact email">
                    <input value={form.contactEmail} onChange={(event) => updateForm("contactEmail", event.target.value)} className="business-center-os-input" />
                  </Field>
                  <Field label="Support email">
                    <input value={form.supportEmail} onChange={(event) => updateForm("supportEmail", event.target.value)} className="business-center-os-input" />
                  </Field>
                  <Field label="Phone number">
                    <input value={form.phoneNumber} onChange={(event) => updateForm("phoneNumber", event.target.value)} className="business-center-os-input" />
                  </Field>
                </div>

                <div className="grid gap-4 rounded-3xl border border-[rgba(255,255,255,.08)] bg-[rgba(255,255,255,.02)] p-4 md:grid-cols-3">
                  <ToggleField label="Show Powered By Business Center OS" checked={form.showPoweredByBusinessCenterOS} onChange={(checked) => updateForm("showPoweredByBusinessCenterOS", checked)} />
                  <ToggleField label="Show Workspace Name" checked={form.showWorkspaceName} onChange={(checked) => updateForm("showWorkspaceName", checked)} />
                  <ToggleField label="Show Company Name" checked={form.showCompanyName} onChange={(checked) => updateForm("showCompanyName", checked)} />
                </div>

                <Field label="Save branding">
                  <button
                    type="submit"
                    disabled={saving}
                    className="business-center-os-button focus-ring inline-flex min-h-11 items-center gap-2 rounded-full px-5 text-sm font-extrabold disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Plus className="size-4" aria-hidden="true" />
                    {saving ? "Saving..." : "Save profile"}
                  </button>
                </Field>
              </form>
            </article>

            <article className="business-center-os-card grid gap-5 p-5">
              <div>
                <p className="text-[0.68rem] font-black uppercase tracking-[0.22em] text-[#C4B5FD]">Document branding</p>
                <h2 className="mt-2 text-2xl font-black text-white">Documents, signatures, and contact blocks</h2>
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                <Field label="Document logo"><input value={form.documentLogoUrl} onChange={(event) => updateForm("documentLogoUrl", event.target.value)} className="business-center-os-input" /></Field>
                <Field label="Footer text"><input value={form.footerText} onChange={(event) => updateForm("footerText", event.target.value)} className="business-center-os-input" /></Field>
                <Field label="Signature block"><textarea value={form.signatureBlock} onChange={(event) => updateForm("signatureBlock", event.target.value)} className="business-center-os-input min-h-28" /></Field>
                <Field label="Company address"><textarea value={form.companyAddress} onChange={(event) => updateForm("companyAddress", event.target.value)} className="business-center-os-input min-h-28" /></Field>
                <Field label="Company contact information"><textarea value={form.companyContactInformation} onChange={(event) => updateForm("companyContactInformation", event.target.value)} className="business-center-os-input min-h-28" /></Field>
              </div>
            </article>

            <article className="business-center-os-card grid gap-5 p-5">
              <div>
                <p className="text-[0.68rem] font-black uppercase tracking-[0.22em] text-[#C4B5FD]">Email branding</p>
                <h2 className="mt-2 text-2xl font-black text-white">Welcome, onboarding, project, and invoice templates</h2>
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                <Field label="Email logo"><input value={form.emailLogoUrl} onChange={(event) => updateForm("emailLogoUrl", event.target.value)} className="business-center-os-input" /></Field>
                <Field label="Header color"><input value={form.emailHeaderColor} onChange={(event) => updateForm("emailHeaderColor", event.target.value)} className="business-center-os-input" /></Field>
                <Field label="Footer color"><input value={form.emailFooterColor} onChange={(event) => updateForm("emailFooterColor", event.target.value)} className="business-center-os-input" /></Field>
                <Field label="Email signature"><textarea value={form.emailSignature} onChange={(event) => updateForm("emailSignature", event.target.value)} className="business-center-os-input min-h-28" /></Field>
                <Field label="Company footer"><textarea value={form.emailCompanyFooter} onChange={(event) => updateForm("emailCompanyFooter", event.target.value)} className="business-center-os-input min-h-28" /></Field>
              </div>
            </article>

            <article className="business-center-os-card grid gap-5 p-5">
              <div>
                <p className="text-[0.68rem] font-black uppercase tracking-[0.22em] text-[#C4B5FD]">Domain configuration</p>
                <h2 className="mt-2 text-2xl font-black text-white">Custom domains and login endpoints</h2>
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                <Field label="Primary domain"><input value={form.primaryDomain} onChange={(event) => updateForm("primaryDomain", event.target.value)} className="business-center-os-input" /></Field>
                <Field label="Portal domain"><input value={form.portalDomain} onChange={(event) => updateForm("portalDomain", event.target.value)} className="business-center-os-input" /></Field>
                <Field label="Custom subdomain"><input value={form.customSubdomain} onChange={(event) => updateForm("customSubdomain", event.target.value)} className="business-center-os-input" /></Field>
                <Field label="Login URL"><input value={form.loginUrl} onChange={(event) => updateForm("loginUrl", event.target.value)} className="business-center-os-input" /></Field>
              </div>
            </article>
          </div>

          <div className="grid gap-6">
            <article className="business-center-os-card sticky top-28 grid gap-4 p-5">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[0.68rem] font-black uppercase tracking-[0.22em] text-[#C4B5FD]">Live preview</p>
                  <h2 className="mt-2 text-2xl font-black text-white">Portal, login, dashboard, email</h2>
                </div>
                <Globe2 className="size-8 text-[#C4B5FD]" aria-hidden="true" />
              </div>

              <div className="grid gap-4">
                <PreviewPane title="Portal preview" icon={Monitor}>
                  <div className="rounded-3xl border p-4" style={{ borderColor: previews.portal.colors.primary }}>
                    <p className="text-xs font-black uppercase tracking-[0.2em]" style={{ color: previews.portal.colors.accent }}>
                      {previews.portal.title}
                    </p>
                    <h3 className="mt-2 text-2xl font-black text-white">{previews.portal.subtitle}</h3>
                    <p className="mt-2 text-sm leading-6 text-white/80">{previews.portal.welcomeMessage}</p>
                  </div>
                </PreviewPane>

                <PreviewPane title="Login preview" icon={Link2}>
                  <div className="rounded-3xl border border-white/10 bg-[#0F1D2B] p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex size-12 items-center justify-center rounded-2xl bg-white/10 text-white">
                        {form.publicDisplayName.slice(0, 1) || "B"}
                      </div>
                      <div>
                        <p className="text-xs font-black uppercase tracking-[0.2em] text-[#C4B5FD]">{previews.login.title}</p>
                        <p className="text-sm text-white/75">{previews.login.subtitle}</p>
                      </div>
                    </div>
                    <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/75">
                      Login URL: {form.loginUrl || "https://app.client.com/login"}
                    </div>
                  </div>
                </PreviewPane>

                <PreviewPane title="Dashboard preview" icon={Paintbrush}>
                  <div className="rounded-3xl border border-white/10 bg-[#101827] p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-black uppercase tracking-[0.2em] text-[#C4B5FD]">{previews.dashboard.badge}</p>
                        <h3 className="mt-2 text-2xl font-black text-white">{previews.dashboard.title}</h3>
                      </div>
                      <div className="rounded-2xl border border-white/10 px-3 py-2 text-xs font-black uppercase tracking-[0.18em] text-white/70">
                        {form.showWorkspaceName ? "Workspace shown" : "Workspace hidden"}
                      </div>
                    </div>
                    <p className="mt-3 text-sm leading-6 text-white/70">{previews.dashboard.subtitle}</p>
                  </div>
                </PreviewPane>

                <PreviewPane title="Email preview" icon={Mail}>
                  <div className="overflow-hidden rounded-3xl border border-white/10 bg-white">
                    <div className="px-4 py-3 text-sm font-black text-white" style={{ background: previews.email.headerColor }}>
                      Welcome Email
                    </div>
                    <div className="grid gap-3 px-4 py-4 text-sm text-[#0F172A]">
                      <p className="font-black">{previews.email.subject}</p>
                      <p>{form.portalWelcomeMessage || "Welcome to your branded Business Center experience."}</p>
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-500">{form.emailSignature || previews.email.signature}</p>
                    </div>
                    <div className="px-4 py-3 text-xs text-white" style={{ background: previews.email.footerColor }}>
                      {form.emailCompanyFooter || "Company footer will appear here."}
                    </div>
                  </div>
                </PreviewPane>
              </div>

              <div className="grid gap-3 rounded-3xl border border-[rgba(168,85,247,.18)] bg-[rgba(255,255,255,.03)] p-4 text-sm font-semibold text-[#D9E6F2]">
                <div className="flex items-center justify-between gap-3">
                  <span>Portal name</span>
                  <span className="text-white">{form.portalName || previewProfile.portal.name}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span>Portal domain</span>
                  <span className="text-white">{form.portalDomain || previewProfile.domains.portalDomain}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span>Powered by Business Center OS</span>
                  <span className="text-white">{form.showPoweredByBusinessCenterOS ? "Shown" : "Hidden"}</span>
                </div>
              </div>
            </article>

            <article className="business-center-os-card grid gap-4 p-5">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[0.68rem] font-black uppercase tracking-[0.22em] text-[#C4B5FD]">Brand events</p>
                  <h2 className="mt-2 text-2xl font-black text-white">Automation-ready change history</h2>
                </div>
                <Bell className="size-8 text-[#C4B5FD]" aria-hidden="true" />
              </div>
              <div className="grid gap-3">
                {(selectedProfile?.events || []).slice(-4).map((event) => (
                  <div key={`${event.type}-${event.createdAt}`} className="rounded-2xl border border-[rgba(255,255,255,.08)] bg-[rgba(255,255,255,.03)] p-4">
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-[#C4B5FD]">{event.type}</p>
                    <p className="mt-2 text-sm font-semibold text-white">{event.summary}</p>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#94A3B8]">{formatDate(event.createdAt)}</p>
                  </div>
                ))}
              </div>
            </article>

            <article className="business-center-os-card grid gap-4 p-5">
              <div>
                <p className="text-[0.68rem] font-black uppercase tracking-[0.22em] text-[#C4B5FD]">Integration map</p>
                <h2 className="mt-2 text-2xl font-black text-white">Who reads this branding profile?</h2>
              </div>
              <ul className="grid gap-2 text-sm font-semibold text-[#D9E6F2]">
                <li className="flex items-start gap-2"><Users className="mt-0.5 size-4 text-[#C4B5FD]" aria-hidden="true" />Universal User Management can apply identity labels and access-level messaging.</li>
                <li className="flex items-start gap-2"><Globe2 className="mt-0.5 size-4 text-[#C4B5FD]" aria-hidden="true" />Universal Client Portal can read portal titles, colors, and welcome content.</li>
                <li className="flex items-start gap-2"><FileText className="mt-0.5 size-4 text-[#C4B5FD]" aria-hidden="true" />Documents, invoices, statements, and letters can inherit company footer and signature data.</li>
                <li className="flex items-start gap-2"><ShieldCheck className="mt-0.5 size-4 text-[#C4B5FD]" aria-hidden="true" />Brand events are ready to become automation triggers later without changing the schema.</li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      {(message || error) && (
        <div className="fixed bottom-4 right-4 z-50 max-w-md rounded-3xl border border-[rgba(168,85,247,.2)] bg-[#08111A]/95 p-4 shadow-[0_24px_60px_rgba(0,0,0,.35)] backdrop-blur-xl">
          <p className={`text-sm font-semibold ${error ? "text-[#FCA5A5]" : "text-[#C4B5FD]"}`}>{error || message}</p>
        </div>
      )}

      {loading ? (
        <div className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center bg-[#07111A]/60 backdrop-blur-sm">
          <div className="rounded-3xl border border-white/10 bg-[#0B1622] px-5 py-4 text-sm font-semibold text-[#D9E6F2] shadow-[0_24px_60px_rgba(0,0,0,.35)]">
            Loading branding workspace...
          </div>
        </div>
      ) : null}
    </BusinessCenterOSAdminShell>
  );
}

function buildFormFromProfile(profile: BusinessCenterBrandingProfileRecord): BrandFormState {
  return {
    companyId: profile.companyId,
    companyName: profile.companyName,
    companySlug: slugify(profile.companyName),
    publicDisplayName: profile.publicDisplayName,
    logoUrl: profile.logoUrl,
    faviconUrl: profile.faviconUrl,
    primaryColor: profile.primaryColor,
    secondaryColor: profile.secondaryColor,
    accentColor: profile.accentColor,
    contactEmail: profile.contactEmail,
    supportEmail: profile.supportEmail,
    phoneNumber: profile.phoneNumber,
    website: profile.website,
    companyDescription: profile.companyDescription,
    portalName: profile.portal.name,
    portalSubtitle: profile.portal.subtitle,
    portalLogoUrl: profile.portal.logoUrl,
    portalPrimaryColor: profile.portal.primaryColor,
    portalSecondaryColor: profile.portal.secondaryColor,
    portalAccentColor: profile.portal.accentColor,
    portalWelcomeMessage: profile.portal.welcomeMessage,
    documentLogoUrl: profile.documents.logoUrl,
    footerText: profile.documents.footerText,
    signatureBlock: profile.documents.signatureBlock,
    companyAddress: profile.documents.companyAddress,
    companyContactInformation: profile.documents.companyContactInformation,
    emailLogoUrl: profile.email.logoUrl,
    emailHeaderColor: profile.email.headerColor,
    emailFooterColor: profile.email.footerColor,
    emailSignature: profile.email.signature,
    emailCompanyFooter: profile.email.companyFooter,
    primaryDomain: profile.domains.primaryDomain,
    portalDomain: profile.domains.portalDomain,
    customSubdomain: profile.domains.customSubdomain,
    loginUrl: profile.domains.loginUrl,
    showPoweredByBusinessCenterOS: profile.toggles.showPoweredByBusinessCenterOS,
    showWorkspaceName: profile.toggles.showWorkspaceName,
    showCompanyName: profile.toggles.showCompanyName,
  };
}

function buildProfileFromForm(
  form: BrandFormState,
  existingEvents: BusinessCenterBrandingProfileRecord["events"],
  sourceOnboardingId: string | null,
): BusinessCenterBrandingProfileRecord {
  const now = new Date().toISOString();
  return createBusinessCenterBrandingProfile({
    companyId: form.companyId || "company-branding-draft",
    companyName: form.companyName || "Untitled Company",
    companySlug: form.companySlug || slugify(form.companyName || "untitled-company"),
    publicDisplayName: form.publicDisplayName || form.companyName || "Untitled Company",
    logoUrl: form.logoUrl,
    faviconUrl: form.faviconUrl,
    primaryColor: form.primaryColor,
    secondaryColor: form.secondaryColor,
    accentColor: form.accentColor,
    contactEmail: form.contactEmail,
    supportEmail: form.supportEmail,
    phoneNumber: form.phoneNumber,
    website: form.website,
    companyDescription: form.companyDescription,
    portal: {
      name: form.portalName || `${form.publicDisplayName || form.companyName || "Brand"} Portal`,
      subtitle: form.portalSubtitle || "Client portal",
      logoUrl: form.portalLogoUrl || form.logoUrl,
      primaryColor: form.portalPrimaryColor,
      secondaryColor: form.portalSecondaryColor,
      accentColor: form.portalAccentColor,
      welcomeMessage: form.portalWelcomeMessage,
    },
    documents: {
      logoUrl: form.documentLogoUrl || form.logoUrl,
      footerText: form.footerText,
      signatureBlock: form.signatureBlock,
      companyAddress: form.companyAddress,
      companyContactInformation: form.companyContactInformation || form.contactEmail,
    },
    email: {
      logoUrl: form.emailLogoUrl || form.logoUrl,
      headerColor: form.emailHeaderColor,
      footerColor: form.emailFooterColor,
      signature: form.emailSignature,
      companyFooter: form.emailCompanyFooter,
    },
    domains: {
      primaryDomain: form.primaryDomain,
      portalDomain: form.portalDomain,
      customSubdomain: form.customSubdomain,
      loginUrl: form.loginUrl,
    },
    toggles: {
      showPoweredByBusinessCenterOS: form.showPoweredByBusinessCenterOS,
      showWorkspaceName: form.showWorkspaceName,
      showCompanyName: form.showCompanyName,
    },
    sourceOnboardingId,
  });
}

function buildProfileFromCompany(company: BusinessCenterCompanyRecord, onboarding: BusinessCenterOnboardingRecord[]) {
  const onboardingRecord = onboarding.find((record) => record.companyId === company.id) || null;
  const now = new Date().toISOString();
  return createBusinessCenterBrandingProfile({
    companyId: company.id,
    companyName: company.companyName,
    companySlug: company.companySlug,
    publicDisplayName: onboardingRecord?.branding.companyName || company.companyName,
    logoUrl: onboardingRecord?.branding.logo || `${company.companySlug}-logo.svg`,
    faviconUrl: `${company.companySlug}-favicon.ico`,
    primaryColor: onboardingRecord?.branding.primaryColor || "#1F2937",
    secondaryColor: onboardingRecord?.branding.accentColor || "#334155",
    accentColor: onboardingRecord?.branding.accentColor || "#06B6D4",
    contactEmail: onboardingRecord?.branding.contactEmail || `hello@${company.companySlug}.com`,
    supportEmail: `support@${company.companySlug}.com`,
    phoneNumber: onboardingRecord?.branding.contactPhone || "",
    website: `https://${company.companySlug}.com`,
    companyDescription: `${company.companyName} branding profile.`,
    portal: {
      name: `${onboardingRecord?.branding.companyName || company.companyName} Portal`,
      subtitle: `Client portal for ${company.companyName}`,
      logoUrl: onboardingRecord?.branding.logo || `${company.companySlug}-logo.svg`,
      primaryColor: onboardingRecord?.branding.primaryColor || "#1F2937",
      secondaryColor: onboardingRecord?.branding.accentColor || "#334155",
      accentColor: onboardingRecord?.branding.accentColor || "#06B6D4",
      welcomeMessage: onboardingRecord?.notes || "Welcome to your branded Business Center portal.",
    },
    documents: {
      logoUrl: onboardingRecord?.branding.logo || `${company.companySlug}-logo.svg`,
      footerText: `${company.companyName} • Confidential client materials`,
      signatureBlock: `${company.companyName}\nBusiness Center OS`,
      companyAddress: `${company.companyName} headquarters`,
      companyContactInformation: onboardingRecord?.branding.contactEmail || `hello@${company.companySlug}.com`,
    },
    email: {
      logoUrl: onboardingRecord?.branding.logo || `${company.companySlug}-logo.svg`,
      headerColor: onboardingRecord?.branding.primaryColor || "#1F2937",
      footerColor: onboardingRecord?.branding.accentColor || "#334155",
      signature: `${company.companyName} Team`,
      companyFooter: `${company.companyName} is powered by Business Center OS.`,
    },
    domains: {
      primaryDomain: `${company.companySlug}.com`,
      portalDomain: `portal.${company.companySlug}.com`,
      customSubdomain: `${company.companySlug}.business`,
      loginUrl: `https://${company.companySlug}.com/login`,
    },
    toggles: {
      showPoweredByBusinessCenterOS: true,
      showWorkspaceName: true,
      showCompanyName: true,
    },
    sourceOnboardingId: onboardingRecord?.id || null,
  });
}

function useBusinessCenterOSBranding(title: string, description: string) {
  useEffect(() => {
    document.title = `${title} | Business Center OS`;
    document.querySelector('meta[name="description"]')?.setAttribute("content", description);

    const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const previousTheme = themeMeta?.getAttribute("content") || "";
    if (themeMeta) themeMeta.setAttribute("content", "#0F1D2B");

    const faviconHref = buildBusinessCenterFavicon();
    const icons = ['link[rel="icon"]', 'link[rel="shortcut icon"]']
      .map((selector) => document.querySelector<HTMLLinkElement>(selector))
      .filter((link): link is HTMLLinkElement => Boolean(link));
    const previousIcons = icons.map((link) => link.getAttribute("href"));
    icons.forEach((link) => {
      link.setAttribute("href", faviconHref);
      link.setAttribute("type", "image/svg+xml");
    });

    return () => {
      if (themeMeta && previousTheme) themeMeta.setAttribute("content", previousTheme);
      icons.forEach((link, index) => {
        const previousHref = previousIcons[index];
        if (previousHref) link.setAttribute("href", previousHref);
      });
    };
  }, [description, title]);
}

function buildBusinessCenterFavicon() {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" role="img" aria-label="Business Center OS">
      <defs>
        <linearGradient id="g" x1="8" y1="8" x2="56" y2="56" gradientUnits="userSpaceOnUse">
          <stop offset="0" stop-color="#A855F7"/>
          <stop offset=".52" stop-color="#14B8A6"/>
          <stop offset="1" stop-color="#06B6D4"/>
        </linearGradient>
      </defs>
      <rect x="4" y="4" width="56" height="56" rx="16" fill="#0F1D2B"/>
      <path d="M16 16h18c9 0 16 6.5 16 14.5S43 45 34 45H16z" fill="url(#g)"/>
      <text x="32" y="39" text-anchor="middle" font-size="16" font-family="Arial, Helvetica, sans-serif" font-weight="800" fill="#F8FAFC">BC</text>
    </svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="grid gap-2">
      <span className="text-xs font-black uppercase tracking-[0.2em] text-[#A78BFA]">{label}</span>
      {children}
    </label>
  );
}

function ToggleField({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-1 size-4 rounded border-white/20 bg-transparent text-violet-400"
      />
      <span className="text-sm font-semibold text-white">{label}</span>
    </label>
  );
}

function PreviewPane({ title, icon: Icon, children }: { title: string; icon: LucideIcon; children: ReactNode }) {
  return (
    <div className="rounded-3xl border border-[rgba(168,85,247,.12)] bg-[rgba(255,255,255,.02)] p-4">
      <div className="mb-3 flex items-center gap-2">
        <span className="inline-flex size-8 items-center justify-center rounded-full border border-[rgba(168,85,247,.2)] bg-[rgba(168,85,247,.08)] text-[#C4B5FD]">
          <Icon className="size-4" aria-hidden="true" />
        </span>
        <p className="text-sm font-black uppercase tracking-[0.18em] text-[#C4B5FD]">{title}</p>
      </div>
      {children}
    </div>
  );
}

function slugify(value: string) {
  return (
    value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .replace(/-{2,}/g, "-") || "brand"
  );
}
